// Module 8 — Monthly Budget Meeting
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// RDS
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import QrCodePrompt from '../QrCodePrompt';

// Types
import { WorkshopActionItemDefinition } from '../../types';

const ADJUSTING_YOUR_BUDGET_VIDEO =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/adjusting-your-budget.mp4';
const ADJUSTING_YOUR_BUDGET_THUMBNAIL =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/adjusting-you-budget.png';
const CLOSE_OUT_YOUR_BUDGET_VIDEO =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/close-out-your-budget.mp4';
const CLOSE_OUT_YOUR_BUDGET_THUMBNAIL =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/close-out-your-budget.png';
const BUDGET_MEETING_GUIDE_URL =
  'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/budget-meeting.pdf';

function TheBudgetCommittee() {
  return (
    <div className="flex flex-col gap-2">
      <Text className="text-4 leading-lg">
        A budget committee meeting is a dedicated time each month — before the
        month begins — where you and your spouse or accountability partner build
        your budget together, align on goals, and make sure every dollar has a
        job before it is spent.
      </Text>
      <div>
        <Text className="text-4 leading-lg">A typical meeting covers:</Text>
        <ul className="text-4 leading-lg list-disc pl-5">
          <li>Listing your income and expenses</li>
          <li>Zeroing out your budget</li>
          <li>Reviewing your financial goals</li>
          <li>Deciding how you will track spending for the month</li>
        </ul>
      </div>
      <Text className="text-4 leading-lg">
        The whole thing should take about 30 minutes.
      </Text>
    </div>
  );
}

function AdjustingYourBudgetClip() {
  return (
    <HLSVideoPlayer
      src={ADJUSTING_YOUR_BUDGET_VIDEO}
      thumbnail={ADJUSTING_YOUR_BUDGET_THUMBNAIL}
    />
  );
}

function CloseOutYourBudgetClip() {
  return (
    <HLSVideoPlayer
      src={CLOSE_OUT_YOUR_BUDGET_VIDEO}
      thumbnail={CLOSE_OUT_YOUR_BUDGET_THUMBNAIL}
    />
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: 'How often do you review your budget throughout the month? Do you wait until the end, or check in as you spend?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'What would need to be true for your budget meeting to actually happen consistently every month?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: "What's the hardest part of having a money conversation with someone you're close to? How do you navigate disagreements?"
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

function BudgetMeetingGuide() {
  return (
    <QrCodePrompt
      activityUrl={BUDGET_MEETING_GUIDE_URL}
      slug="budget-meeting-guide"
      dialogTitle="Budget Committee Meeting Guide QR Code"
      linkLabel="View Guide"
    >
      <Text className="text-5 text-blue-90 leading-lg font-bold">
        Scan the QR code to download your budget committee meeting guide!
      </Text>
      <Text className="text-4 leading-lg">
        Use this guide to run your first budget committee meeting with
        confidence.
      </Text>
    </QrCodePrompt>
  );
}

const actionItems: WorkshopActionItemDefinition[] = [
  { title: 'The Budget Committee', Content: TheBudgetCommittee },
  {
    title: 'Adjusting Your Budget',
    Content: AdjustingYourBudgetClip,
    icon: VideoOutlinedIcon,
    duration: '2:17'
  },
  {
    title: 'Close Out Your Budget',
    Content: CloseOutYourBudgetClip,
    icon: VideoOutlinedIcon,
    duration: '3:26'
  },
  { title: 'Discussion', Content: Discussion },
  { title: 'Budget Meeting Guide', Content: BudgetMeetingGuide }
];

export default actionItems;
